import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Event } from '@/lib/types';
import { Loader2 } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { CustomerFormData } from './forms/types';
import { useNavigate } from 'react-router-dom';

interface QRPhButtonProps {
  event: Event;
  form: UseFormReturn<CustomerFormData>;
  vipSeats: string[];
  standingTickets: number;
  earlyBirdTickets: number;
  totalAmount: number;
}

export const QRPhButton: React.FC<QRPhButtonProps> = ({
  event,
  form,
  vipSeats,
  standingTickets,
  earlyBirdTickets,
  totalAmount
}) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Only show for PHP currency
  if (event.currency !== 'PHP') {
    return null;
  }

  const handleQRPhPayment = async () => {
    // Validate form before proceeding
    const isValid = await form.trigger();
    if (!isValid) {
      toast({
        title: "Form Validation Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive",
      });
      return;
    }

    const formData = form.getValues();
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-paymongo-intent', {
        body: {
          amount: totalAmount,
          currency: event.currency,
          customer: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone: `${formData.phoneCountryCode}${formData.phoneNumber}`,
          },
          eventId: event.id,
          vipSeats,
          standingTickets,
          earlyBirdTickets,
        }
      });

      if (error) throw error;

      if (data.success) {
        const params = new URLSearchParams({
          qr_code_url: data.qr_code_url,
          expires_at: data.expires_at.toString(),
          amount: totalAmount.toString(),
          payment_intent_id: data.payment_intent_id,
          // Store order data for creation after successful payment
          order_data: JSON.stringify(data.order_data)
        });
        navigate(`/qr-payment?${params.toString()}`);
        // Scroll to top after navigation
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      } else {
        throw new Error(data.error || 'Failed to create QRPh payment');
      }
    } catch (error) {
      console.error('QRPh payment error:', error);
      toast({
        title: "Payment Error",
        description: "Failed to create QRPh payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button
        onClick={handleQRPhPayment}
        disabled={loading}
        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6"
        aria-label="Pay with QRPh"
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <img 
            src="https://cloudflare.slay.events/QRPh.svg" 
            alt="QRPh" 
            className="mr-2 h-6 w-auto"
          />
        )}
        {loading ? 'Generating QR Code...' : 'Pay with QRPh'}
      </Button>
      
      <div className="flex justify-center">
        <img 
          src="https://cloudflare.slay.events/QRPHadjusted.png" 
          alt="Accepted banks for QRPh" 
          className="h-18 w-auto opacity-75"
        />
      </div>
    </div>
  );
};